/**
 * Bloco "Posicao aberta projetada (estimativa)" no Fluxo (Task #50 Fase C).
 *
 * Renderizado SEPARADO das linhas bilaterais — header secundario distinto,
 * tooltip explicativo no icone "i", soma dos valores ao final. Cada linha
 * mostra mes_ref + submercado + tipo + dt_pagamento_estimada + valor com
 * sinal natural (positivo = sobra, negativo = excesso a pagar).
 *
 * Decisao Veiga 29/04: nao somar com bilateral; tornar a estimativa
 * VISUALMENTE distinta pra usuario nao confundir com numero auditavel.
 */
import { Info } from "lucide-react";
import { usePosicaoAberta } from "../../hooks/usePosicaoAberta";
import type { LinhaPosicaoAberta } from "../../api/types";
import { fmtBRL, fmtMWh, fmtMWm } from "../../utils/formatters";
import { PosicaoBadge } from "./PosicaoBadge";

const TOOLTIP_TEXT =
  "MtM estimado: volume aberto × preço de referência. Mês FECHADO marca a " +
  "PLD realizado (canonical_pld_mensal); mês FUTURO, à curva forward (Zeus). " +
  "Representa o impacto de zerar a posição contra a referência — SHORT vai " +
  "comprar (saída de caixa); LONG vai vender (entrada). Indexados inclusive " +
  "(marcar contra PLD revela o ágio como P&L). Convencional + I0 + I50 + " +
  "I100 (CQ excluído).";

// Task #41: rótulo legível da fonte do preço (LinhaPosicaoAberta.fonte_mtm).
const FONTE_LABEL: Record<string, string> = {
  pld_realizado: "PLD realizado",
  forward: "forward",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function fmtMesRef(iso: string): string {
  const [y, m] = iso.split("-");
  const mesesCurto = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${mesesCurto[parseInt(m, 10) - 1] ?? m}/${y.slice(2)}`;
}

function fmtMesRefLongo(iso: string): string {
  const [y, m] = iso.split("-");
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  return `${meses[parseInt(m, 10) - 1] ?? m}/${y.slice(2)}`;
}

function fmtValor(v: string | null): string {
  if (!v) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return fmtBRL(n, { decimals: 0 });
}

// Task #123: agregacao em memoria pra subtotais no footer. Lista cabe em
// algumas dezenas de linhas (4 submercados × 4 tipos = 16 max combinacoes
// por mes_ref); roda client-side sem latencia perceptivel e sem mexer no
// endpoint backend.
interface Subtotal {
  chave: string; // submercado ou tipo
  vol_mwh: number;
  vol_mwm: number;
  mtm: number;
  tem_mtm: boolean; // se alguma linha tinha MtM (pra distinguir 0 real de null)
}

function _accumular(linhas: LinhaPosicaoAberta[], chaveFn: (l: LinhaPosicaoAberta) => string): Subtotal[] {
  const acc = new Map<string, Subtotal>();
  for (const l of linhas) {
    const chave = chaveFn(l);
    let bucket = acc.get(chave);
    if (!bucket) {
      bucket = { chave, vol_mwh: 0, vol_mwm: 0, mtm: 0, tem_mtm: false };
      acc.set(chave, bucket);
    }
    bucket.vol_mwh += Number(l.volume_liquido || "0");
    bucket.vol_mwm += Number(l.volume_mwm || "0");
    if (l.valor_mtm_estimado !== null && l.valor_mtm_estimado !== undefined) {
      bucket.mtm += Number(l.valor_mtm_estimado);
      bucket.tem_mtm = true;
    }
  }
  return Array.from(acc.values());
}

// Ordem fixa pra apresentar submercados e tipos com layout previsivel
// (Mesa lê de cima pra baixo nessa ordem). Itens sem dado naquele
// fornecimento simplesmente nao renderizam.
const ORDEM_SUBMERCADO = ["SE", "S", "NE", "N"];
const ORDEM_TIPO = ["Convencional", "I0", "I50", "I100"];

function _ordenarPor(subtotais: Subtotal[], ordem: string[]): Subtotal[] {
  const idx = new Map(ordem.map((k, i) => [k, i]));
  return subtotais
    .slice()
    .sort((a, b) => (idx.get(a.chave) ?? 999) - (idx.get(b.chave) ?? 999));
}

// Exportados pra teste; mantém superfície clara e evita acoplar o teste
// aos detalhes do componente.
export const __test__ = { _accumular, _ordenarPor };

export function PosicaoAbertaSection() {
  const { data, isLoading, error } = usePosicaoAberta();
  const linhas = data.linhas_posicao_aberta;

  // Task #123: subtotais em memoria. Lista pequena (< 30 linhas tipico),
  // safe pra agregar a cada render — re-mount do componente acontece em
  // troca de filtro de mes, nao em re-render de hover.
  const subtotaisSubmercado = _ordenarPor(
    _accumular(linhas, (l) => l.submercado),
    ORDEM_SUBMERCADO,
  );
  const subtotaisTipo = _ordenarPor(
    _accumular(linhas, (l) => l.tipo_energia),
    ORDEM_TIPO,
  );

  if (isLoading) {
    return (
      <div
        className="text-white/40 text-xs py-4 text-center"
        data-testid="posicao-aberta-loading"
      >
        Carregando posição aberta…
      </div>
    );
  }

  if (linhas.length === 0) {
    return (
      <div
        className="rounded-xl border border-white/10 bg-indra-azul-surface/40 p-4 mt-6"
        data-testid="posicao-aberta-empty"
      >
        <h3 className="text-white/70 font-semibold text-sm mb-1 flex items-center gap-2">
          Posição aberta projetada (estimativa)
          <Info size={12} className="text-white/40" />
        </h3>
        <p className="text-white/50 text-xs">
          {error
            ? "Erro ao carregar a posição. Recarregue a página."
            : "Nenhuma posição registrada. Rode o sync via run-task: " +
              "python scripts/zeus_volume_total_sync.py"}
        </p>
      </div>
    );
  }

  // Task #83: header agora mostra total MtM (substitui total_estimado, que
  // representava saldo R$ contratado — dupla contagem com Entradas/Saidas).
  const total = Number(data.total_valor_mtm_estimado || "0");
  const totalPositivo = total >= 0;
  const linhasSemMtm = data.linhas_sem_mtm;

  return (
    <div
      className="rounded-xl border border-amber-300/20 bg-amber-400/5 p-4 mt-6"
      data-testid="posicao-aberta-section"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            Posição aberta projetada
            <span className="text-amber-200/80 text-[11px] uppercase tracking-wider">
              estimativa
            </span>
            <span
              className="cursor-help"
              title={TOOLTIP_TEXT}
              data-testid="posicao-aberta-tooltip"
            >
              <Info size={12} className="text-amber-200/70" />
            </span>
          </h3>
          {/* Task #72: header descritivo do escopo (mes_ref + dt_6du) — UI
              fica clara que a tabela mostra so o mes que liquida no proximo 6du. */}
          {data.mes_ref && data.dt_proximo_6du && (
            <p
              className="text-white/50 text-xs mt-1"
              data-testid="posicao-aberta-escopo"
            >
              Fornecimento {fmtMesRefLongo(data.mes_ref)} (liquidação{" "}
              {fmtDate(data.dt_proximo_6du)})
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/50">
            MtM total estimado
          </div>
          <div
            className={
              "text-lg font-black font-sans " +
              (totalPositivo ? "text-indra-positivo" : "text-indra-negativo")
            }
            data-testid="posicao-aberta-total-mtm"
          >
            {totalPositivo ? "+" : ""}
            {fmtValor(data.total_valor_mtm_estimado)}
          </div>
          {linhasSemMtm > 0 && (
            <div
              className="text-[10px] text-amber-200/70 mt-0.5"
              data-testid="posicao-aberta-sem-mtm-aviso"
              title="Linhas sem fonte de preço (nem PLD realizado nem curva forward cobrindo o mês) — MtM não calculado e fora do total."
            >
              {linhasSemMtm}{" "}
              {linhasSemMtm === 1 ? "linha sem curva" : "linhas sem curva"}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/5">
        <table className="w-full text-xs">
          <thead className="bg-indra-azul-deep/60 text-white/60 uppercase tracking-wider">
            <tr>
              <th className="px-2 py-1.5 text-left">Mês ref.</th>
              <th className="px-2 py-1.5 text-left">Submercado</th>
              <th className="px-2 py-1.5 text-left">Tipo</th>
              {/* Task #80b: coluna POSIÇÃO (renomeada de VOLUME) — cell tem
                  badge LONG/SHORT/FLAT + MWh empilhado em cima e MWm embaixo
                  (fonte 70%/opacity 65%). Cor verde/vermelho aplicada na <td>
                  e cascateada via inherit; badge tem cor propria. */}
              <th className="px-2 py-1.5 text-right">Posição</th>
              {/* Task #138: header renomeado de "Preço" → "Tipo de Preço".
                  Mesa apontou que confunde — a coluna mostra Fixo/PLD+/Indexado
                  (categoria de precificacao), nao valor R$. */}
              <th className="px-2 py-1.5 text-left">Tipo de Preço</th>
              <th className="px-2 py-1.5 text-left">Liquidação 6du</th>
              {/* Task #83: coluna VALOR exibe MtM (volume × curva forward) —
                  substitui posicao_valor_estimado (saldo R$ contratado, dupla
                  contagem com Entradas/Saidas bilaterais). Linha sem curva
                  cobrindo o mes_ref vira "—" cinza. */}
              <th className="px-2 py-1.5 text-right">MtM (R$)</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((row: LinhaPosicaoAberta) => {
              const mtmStr = row.valor_mtm_estimado;
              const semMtm = mtmStr === null || mtmStr === undefined;
              const valor = Number(mtmStr || "0");
              const positivo = valor >= 0;
              // Task #41: rótulo da fonte do preço por linha (PLD realizado
              // vs forward). null/ausente quando sem fonte ("sem curva").
              const fonteLabel = row.fonte_mtm
                ? (FONTE_LABEL[row.fonte_mtm] ?? row.fonte_mtm)
                : null;
              // Pos-#80b: volume_liquido vem com sinal certo (compra+venda
              // do canonical). >0 = LONG verde, <0 = SHORT vermelho, =0
              // = FLAT cinza. Cor da td casa com o sinal do volume; valor
              // R$ tem propria cor (pode divergir em casos raros — trade
              // indexado com val_total descolado do volume).
              const volMwh = row.volume_liquido;
              const volMwm = row.volume_mwm;
              const volNum = Number(volMwh || "0");
              const volPositivo = volNum > 0;
              const volNegativo = volNum < 0;
              return (
                <tr
                  key={`${row.mes_ref}-${row.submercado}-${row.tipo_energia}-${row.tipo_preco}`}
                  className="border-t border-white/5 hover:bg-white/5"
                  data-testid="posicao-aberta-row"
                >
                  <td className="px-2 py-1.5 text-white/90">{fmtMesRef(row.mes_ref)}</td>
                  <td className="px-2 py-1.5 text-white/80">{row.submercado}</td>
                  <td className="px-2 py-1.5 text-white/80">{row.tipo_energia}</td>
                  <td
                    className={
                      "px-2 py-1.5 text-right font-mono leading-tight " +
                      (volPositivo
                        ? "text-indra-positivo"
                        : volNegativo
                          ? "text-indra-negativo"
                          : "text-white/60")
                    }
                    data-testid="posicao-aberta-cell-volume"
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <PosicaoBadge
                        volumeMwh={volMwh}
                        testIdPrefix="posicao-aberta-row-badge"
                      />
                      <div className="text-xs">{fmtMWh(volMwh)}</div>
                    </div>
                    <div className="text-[10px] opacity-65">{fmtMWm(volMwm)}</div>
                  </td>
                  <td className="px-2 py-1.5 text-white/60">{row.tipo_preco}</td>
                  <td className="px-2 py-1.5 text-white/70">
                    {fmtDate(row.dt_pagamento_estimada)}
                  </td>
                  <td
                    className={
                      "px-2 py-1.5 text-right font-mono " +
                      (semMtm
                        ? "text-white/40"
                        : positivo
                          ? "text-indra-positivo"
                          : "text-indra-negativo")
                    }
                    data-testid="posicao-aberta-cell-mtm"
                    title={
                      semMtm
                        ? "Sem fonte de preço (nem PLD realizado nem curva forward) para este mês"
                        : fonteLabel
                          ? `Marcado a ${fonteLabel}`
                          : undefined
                    }
                  >
                    {semMtm ? "—" : `${positivo ? "+" : ""}${fmtValor(mtmStr)}`}
                    {!semMtm && fonteLabel && (
                      <div
                        className="text-[9px] font-sans font-normal text-white/40 uppercase tracking-wide"
                        data-testid="posicao-aberta-cell-fonte"
                      >
                        {fonteLabel}
                      </div>
                    )}
                    {row.fonte_posicao === "espelho" && (
                      <div
                        className="text-[9px] font-sans font-normal text-amber-300/80 uppercase tracking-wide"
                        data-testid="posicao-aberta-cell-espelho"
                        title="Posição calculada dos contratos vigentes sem os clientes excluídos (#66): o feed do Zeus é agregado e não permite tirá-los"
                      >
                        {row.fonte_posicao_rotulo ?? "estimativa (espelho)"}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Task #123: subtotais por Submercado + Tipo. Footer aparece
              apenas quando ha mais de 1 linha — pra 1 linha unica o
              "subtotal" seria igual a propria linha (ruido). */}
          {linhas.length > 1 && (
            <tfoot
              className="bg-indra-azul-deep/40 border-t-2 border-white/15"
              data-testid="posicao-aberta-subtotais"
            >
              {/* Cabecalho do grupo Submercado */}
              <tr data-testid="posicao-aberta-subtotal-header-submercado">
                <td
                  colSpan={7}
                  className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/50 bg-indra-azul-deep/60"
                >
                  Total por Submercado
                </td>
              </tr>
              {subtotaisSubmercado.map((s) => {
                const volPos = s.vol_mwh > 0;
                const volNeg = s.vol_mwh < 0;
                const mtmPos = s.mtm >= 0;
                return (
                  <tr
                    key={`subt-sub-${s.chave}`}
                    data-testid={`posicao-aberta-subtotal-submercado-${s.chave}`}
                    className="font-medium"
                  >
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td className="px-2 py-1.5 text-white/90 font-semibold">{s.chave}</td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono leading-tight " +
                        (volPos
                          ? "text-indra-positivo"
                          : volNeg
                            ? "text-indra-negativo"
                            : "text-white/60")
                      }
                    >
                      <div className="text-xs">{fmtMWh(s.vol_mwh.toString())}</div>
                      <div className="text-[10px] opacity-65">{fmtMWm(s.vol_mwm.toString())}</div>
                    </td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono " +
                        (!s.tem_mtm
                          ? "text-white/40"
                          : mtmPos
                            ? "text-indra-positivo"
                            : "text-indra-negativo")
                      }
                      data-testid={`posicao-aberta-subtotal-submercado-${s.chave}-mtm`}
                    >
                      {!s.tem_mtm
                        ? "—"
                        : `${mtmPos ? "+" : ""}${fmtBRL(s.mtm, { decimals: 0 })}`}
                    </td>
                  </tr>
                );
              })}
              {/* Cabecalho do grupo Tipo */}
              <tr data-testid="posicao-aberta-subtotal-header-tipo">
                <td
                  colSpan={7}
                  className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/50 bg-indra-azul-deep/60 border-t border-white/10"
                >
                  Total por Tipo
                </td>
              </tr>
              {subtotaisTipo.map((s) => {
                const volPos = s.vol_mwh > 0;
                const volNeg = s.vol_mwh < 0;
                const mtmPos = s.mtm >= 0;
                return (
                  <tr
                    key={`subt-tipo-${s.chave}`}
                    data-testid={`posicao-aberta-subtotal-tipo-${s.chave}`}
                    className="font-medium"
                  >
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td className="px-2 py-1.5 text-white/90 font-semibold">{s.chave}</td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono leading-tight " +
                        (volPos
                          ? "text-indra-positivo"
                          : volNeg
                            ? "text-indra-negativo"
                            : "text-white/60")
                      }
                    >
                      <div className="text-xs">{fmtMWh(s.vol_mwh.toString())}</div>
                      <div className="text-[10px] opacity-65">{fmtMWm(s.vol_mwm.toString())}</div>
                    </td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td className="px-2 py-1.5 text-white/40">—</td>
                    <td
                      className={
                        "px-2 py-1.5 text-right font-mono " +
                        (!s.tem_mtm
                          ? "text-white/40"
                          : mtmPos
                            ? "text-indra-positivo"
                            : "text-indra-negativo")
                      }
                      data-testid={`posicao-aberta-subtotal-tipo-${s.chave}-mtm`}
                    >
                      {!s.tem_mtm
                        ? "—"
                        : `${mtmPos ? "+" : ""}${fmtBRL(s.mtm, { decimals: 0 })}`}
                    </td>
                  </tr>
                );
              })}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
